import type { FeishuPersonalTask, LicenseCase, LicenseSummary } from '../types'

type LicenseOutcome = Pick<LicenseSummary, 'effectiveStatus' | 'rejectionReason'>

export function licenseStatusLabel(license: LicenseOutcome): string | undefined {
  if (license.effectiveStatus !== 'REJECTED') return undefined
  if (license.rejectionReason?.startsWith('申请人撤回：')) return '已撤回'
  if (license.rejectionReason?.startsWith('签发退回：')) return '签发已退回'
  return '审批未通过'
}

export type LicenseDeliveryStage = 'PREPARING' | 'PENDING_APPROVAL' | 'PENDING_ISSUANCE' | 'PENDING_ACTIVATION' | 'PENDING_RECEIPT' | 'READY' | 'ATTENTION'

export type LicenseDeliveryProgress = {
  stage: LicenseDeliveryStage
  label: string
  description: string
  nextStep: string
  tone: 'neutral' | 'warning' | 'success'
}

/**
 * Keep the business-facing delivery stage separate from raw License and device statuses.
 * A signed artifact is not proof that the target device has completed activation.
 */
export function licenseDeliveryProgress(caseFile: Pick<LicenseCase, 'license' | 'installation' | 'activation'>): LicenseDeliveryProgress {
  const { license, installation, activation } = caseFile
  if (license.effectiveStatus === 'REJECTED') {
    const withdrawn = license.rejectionReason?.startsWith('申请人撤回：')
    const returned = license.rejectionReason?.startsWith('签发退回：')
    return {
      stage: 'ATTENTION',
      label: withdrawn ? '申请已撤回' : returned ? '需要修正后重新申请' : '审批未通过',
      description: license.rejectionReason || '本次申请未进入签发，请查看处理意见。',
      nextStep: withdrawn ? '确认业务信息后重新发起申请。' : '按处理意见修正后重新提交。',
      tone: 'warning',
    }
  }
  if (['EXPIRED', 'REVOKED'].includes(license.effectiveStatus)) return {
    stage: 'ATTENTION', label: '交付已终止', description: license.effectiveStatus === 'REVOKED' ? 'License 已撤销，不能继续交付。' : 'License 或工作流已到期，不能继续交付。', nextStep: '如仍需使用，请联系治理人员核对授权。', tone: 'warning',
  }
  if (license.effectiveStatus === 'PENDING_APPROVAL') return {
    stage: 'PENDING_APPROVAL', label: '等待审批', description: '申请已提交，正在等待审批人查看合同原件并确认权益。', nextStep: '审批通过后会自动进入签发队列。', tone: 'neutral',
  }
  if (license.effectiveStatus === 'PENDING_ISSUANCE') return {
    stage: installation ? 'PENDING_ISSUANCE' : 'ATTENTION',
    label: installation ? '等待签发' : '计划实例异常',
    description: installation ? '审批已完成，签发人将向已审批的计划实例签发 License。' : '申请中固化的计划安装实例无法读取，当前禁止签发。',
    nextStep: installation ? '签发人完成预检和操作验证后签发。' : '核对实例后退回申请人重新提交。',
    tone: installation ? 'neutral' : 'warning',
  }
  if (['RESPONSE_EXPIRED', 'RECEIPT_DEADLINE_EXPIRED'].includes(activation.status)) return {
    stage: 'ATTENTION', label: activation.label, description: activation.message, nextStep: '在目标设备重新导出离线请求，再继续交付。', tone: 'warning',
  }
  if (activation.status === 'RESPONSE_ISSUED') return {
    stage: 'PENDING_RECEIPT', label: '等待设备回执', description: '离线响应已生成；这不等于设备已激活。', nextStep: '在设备导入响应文件，并上传设备签名回执确认。', tone: 'neutral',
  }
  if (activation.status === 'ACTIVE') return {
    stage: 'READY', label: '设备已可用', description: 'License 已签发，目标安装实例也已完成激活。', nextStep: '后续关注在线授权检查和到期提醒。', tone: 'success',
  }
  if (activation.status === 'NOT_BOUND') return {
    stage: 'PREPARING', label: '等待登记计划实例', description: '签发前需要一条属于同一客户和商业授权的计划安装实例。', nextStep: '先登记计划安装实例，再重新发起申请。', tone: 'warning',
  }
  return {
    stage: 'PENDING_ACTIVATION', label: '等待设备激活', description: 'License 已签发，但目标设备还没有完成激活。', nextStep: '下载 License，在设备完成在线或离线激活。', tone: 'neutral',
  }
}

export function feishuTaskAction(task: Pick<FeishuPersonalTask, 'stageCode' | 'nextAction' | 'title'> & { eventType?: string }): { label: string, detail: string } {
  if (task.stageCode === 'APPROVAL') return { label: '请审批 License', detail: '查看授权摘要并逐份查看合同原件后，批准或退回申请。' }
  if (task.stageCode === 'ISSUANCE') return { label: '请签发 License', detail: '核对已审批权益、计划实例和签名预检后完成签发。' }
  if (task.stageCode === 'DELIVERY_ACTIVATION') {
    if (task.eventType === 'OFFLINE_ACTIVATION_REQUEST_RECEIVED') return { label: '请生成设备响应', detail: '已收到设备离线请求，核对后生成并交付响应文件。' }
    if (task.eventType === 'OFFLINE_ACTIVATION_RESPONSE_AVAILABLE') return { label: '请交付设备响应', detail: '下载离线响应文件并导入目标设备。' }
    if (task.eventType === 'OFFLINE_ACTIVATION_RECEIPT_REQUIRED') return { label: '请确认设备回执', detail: '设备导入完成后，上传签名回执以确认激活。' }
    return { label: '请继续设备交付', detail: '完成 License 导入、设备激活和回执确认。' }
  }
  if (task.stageCode === 'VALIDATION_EXCEPTION') return { label: '请处理授权异常', detail: '查看设备授权检查的拒绝原因并记录处理结果。' }
  if (task.stageCode === 'RENEWAL') return { label: '请处理续期事项', detail: '核对到期时间和后续商业授权，避免客户服务中断。' }
  return { label: task.nextAction || '请处理此任务', detail: task.title }
}
